import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, TrendingUp, Users, BarChart3, Calendar, Shield } from 'lucide-react';

const TAB_CLASS = (active) => `px-2.5 py-1 text-[10px] font-medium rounded-full cursor-pointer transition-colors whitespace-nowrap ${active ? 'tint-brand font-semibold' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`;

const STATUS_COLORS = {
  submitted: '#3B82F6', under_review: '#F3CF5B', approved: '#10B981',
  partially_approved: '#7EC9C9', rejected: '#E44E50', cancelled: '#6B7280',
  cancellation_requested: '#F59E0B', pending_sponsor_approval: '#6763A9', delivered: '#7EC9C9',
};

const TABS = [
  { key: 'topSponsors', label: 'Top Sponsors', icon: Users },
  { key: 'rightsUtil', label: 'Rights Usage', icon: BarChart3 },
  { key: 'byRound', label: 'By Round', icon: Calendar },
  { key: 'limits', label: 'Limits', icon: AlertTriangle },
  { key: 'overdue', label: 'Overdue', icon: Shield },
];

export function SystemOverviewCard({ data }) {
  const [tab, setTab] = useState('topSponsors');
  const navigate = useNavigate();

  // Never render nothing — an empty grid cell leaves a hole in the dashboard.
  if (!data) {
    return (
 <Card data-testid="system-overview-card-empty" className={`bg-white dark:bg-[var(--surface)] shadow-card flex flex-col rounded-card lg:h-full`}>
        <div className="flex items-center gap-2 px-5 pt-4 pb-1">
          <TrendingUp size={14} className="text-gray-400" />
          <span className="section-title flex-1">System Overview</span>
        </div>
        <div className="flex gap-1 px-5 py-1.5">
          {TABS.map(t => (
            <span key={t.key} className={`${TAB_CLASS(false)} opacity-40 cursor-default`}>{t.label}</span>
          ))}
        </div>
        <CardContent className="flex-1 flex flex-col items-center justify-center gap-2 px-5">
          <TrendingUp size={22} className="text-gray-200 dark:text-gray-700" />
          <p className="text-xs text-gray-400 dark:text-gray-500">No data available</p>
          <p className="text-[10px] text-gray-300 dark:text-gray-600 text-center max-w-[180px] leading-relaxed">
            Analytics appear here once sponsors are assigned to you
          </p>
        </CardContent>
      </Card>
    );
  }

  const alertCount = (data.near_limit_alerts?.length || 0) + (data.overdue_requests?.length || 0);

  return (
 <Card data-testid="system-overview-card" className={`bg-white dark:bg-[var(--surface)] shadow-card flex flex-col rounded-card lg:h-full`}>
      <div className="flex items-center gap-2 px-5 pt-4 pb-1">
        <TrendingUp size={14} className="text-gray-400" />
        <span className="section-title flex-1">System Overview</span>
        {alertCount > 0 && <Badge className="text-[9px] px-1.5 py-0 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-0">{alertCount} alerts</Badge>}
        {(data.unassigned_count || 0) > 0 && <Badge className="text-[9px] px-1.5 py-0 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-0">{data.unassigned_count} unassigned</Badge>}
      </div>
      <div className="flex gap-1 px-5 py-1.5 overflow-x-auto scrollbar-thin">
        {TABS.map(t => (
          <button key={t.key} className={TAB_CLASS(tab === t.key)} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>
      <CardContent className="px-5 pt-2 pb-3 flex-1 min-h-0 flex flex-col">
        {/* ── Top Sponsors ── */}
        {tab === 'topSponsors' && (
          <div className="card-scroll">
            {(data.top_sponsors || []).map((s, i) => (
              <div key={s.id} className="card-row flex items-center gap-2 cursor-pointer px-1 py-1.5" onClick={() => navigate(`/sponsors/${s.id}`)}>
                <span className="text-[10px] text-gray-400 w-4 text-right tabular-nums">{i + 1}</span>
                {s.logo_url ? <img src={s.logo_url} alt="" className="w-5 h-5 rounded object-cover" /> : <div className="w-5 h-5 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[9px] font-bold text-gray-400">{s.name?.[0]}</div>}
                <span className="flex-1 text-xs text-gray-700 dark:text-gray-300 truncate">{s.name}</span>
                <span className="text-xs font-bold text-gray-800 dark:text-gray-200 tabular-nums">{s.count}</span>
              </div>
            ))}
            {(!data.top_sponsors?.length) && <p className="text-xs text-gray-400 text-center py-4">No data</p>}
          </div>
        )}

        {/* ── Rights Utilization ── */}
        {tab === 'rightsUtil' && (
          <div className="card-scroll space-y-2">
            {(data.rights_utilization || []).slice(0, 12).map(r => (
              <div key={r.name}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[11px] text-gray-600 dark:text-gray-400 truncate flex-1">{r.name}</span>
                  <span className="text-[10px] text-gray-400 tabular-nums ml-2">
                    {r.unlimited && !r.allocated
                      ? `${r.used} used · ∞`
                      : r.unlimited
                        ? `${r.used}/${r.allocated}+∞ (${r.pct}%)`
                        : `${r.used}/${r.allocated} (${r.pct}%)`}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 dark:bg-[var(--chip)] rounded-full overflow-hidden">
                  {r.unlimited && !r.allocated ? (
                    <div className="h-full w-full bg-emerald-500/30 dark:bg-emerald-400/30 rounded-full" />
                  ) : (
                    <div className="h-full bg-[var(--brand)] dark:bg-white rounded-full transition-all" style={{ width: `${Math.min(100, r.pct)}%` }} />
                  )}
                </div>
              </div>
            ))}
            {(!data.rights_utilization?.length) && <p className="text-xs text-gray-400 text-center py-4">No data</p>}
          </div>
        )}

        {/* ── By Round ── */}
        {tab === 'byRound' && (
          <div className="card-scroll">
            {(data.requests_per_round || []).map(r => {
              const maxCount = Math.max(...(data.requests_per_round || []).map(x => x.count), 1);
              return (
                <div key={r.round} className="flex items-center gap-2">
                  <span className="text-[11px] text-gray-600 dark:text-gray-400 w-20 shrink-0 truncate">{r.round}</span>
                  <div className="flex-1 h-2 bg-gray-100 dark:bg-[var(--chip)] rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 dark:bg-blue-400 rounded-full" style={{ width: `${(r.count / maxCount) * 100}%` }} />
                  </div>
                  <span className="text-xs font-bold text-gray-700 dark:text-gray-300 tabular-nums w-6 text-right">{r.count}</span>
                </div>
              );
            })}
            {(!data.requests_per_round?.length) && <p className="text-xs text-gray-400 text-center py-4">No data</p>}
          </div>
        )}

        {/* ── Rights Limits ── */}
        {tab === 'limits' && (
          <div className="card-scroll">
            {(data.near_limit_alerts || []).map((a, i) => (
              <div key={i} className="card-row flex items-center gap-2 py-1.5 px-1">
                <div className={`w-2 h-2 rounded-full shrink-0 ${a.pct >= 100 ? 'bg-red-500' : 'bg-amber-500'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-gray-700 dark:text-gray-300 truncate">{a.sponsor_name} — {a.right_name}</p>
                  <p className="text-[9px] text-gray-400">{a.used}/{a.allocated} used</p>
                </div>
                <span className={`text-xs font-bold tabular-nums ${a.pct >= 100 ? 'text-red-500' : 'text-amber-500'}`}>{a.pct}%</span>
              </div>
            ))}
            {(!data.near_limit_alerts?.length) && <p className="text-xs text-gray-400 text-center py-4">No sponsors near limits</p>}
          </div>
        )}

        {/* ── Overdue ── */}
        {tab === 'overdue' && (
          <div className="card-scroll">
            {(data.overdue_requests || []).map((r, i) => (
              <div key={i} className="card-row flex items-center gap-2 py-1.5 px-1 cursor-pointer" onClick={() => navigate(`/requests/${r.request_id}`)}>
                <Clock size={12} className="text-red-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-gray-700 dark:text-gray-300">#{r.request_number} — {r.sponsor_name}</p>
                  <p className="text-[9px] text-gray-400">Submitted {r.submitted_at?.split('T')[0]}</p>
                </div>
                <Badge className="text-[9px] px-1.5 py-0 bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400 border-0">{r.hours_old}h ago</Badge>
              </div>
            ))}
            {(!data.overdue_requests?.length) && <p className="text-xs text-gray-400 text-center py-4">No overdue requests</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
